import { set, get } from 'lib/storage/versionedLocalStorageUtils'
import { EmptyObject } from 'lib/object/objectUtils'

const MIN_VIEW_GAP = 1000 * 60 * 5 // 5 minutes
const VIEW_RESET_GAP = 1000 * 60 * 60 * 24 * 30 // 30 days

interface OfferViewCountData {
  viewCount: number
  lastViewedAt: number
  savePromptShownAt?: number
}

interface OfferViewCounts {
  [offerId: string]: OfferViewCountData
}

const KEY = 'offerViewCountForSavePrompt'
const VERSION = 1

function getOfferViewCounts(): OfferViewCounts {
  return get(KEY, VERSION) ?? EmptyObject
}

function getOfferViewCountData(offerId: string) {
  const viewCounts = getOfferViewCounts()
  return viewCounts[offerId]
}

function saveOfferViewCounts(viewCounts: OfferViewCounts) {
  set(KEY, VERSION, viewCounts)
}

function saveOfferViewCountData(offerId: string, viewCountData: OfferViewCountData) {
  saveOfferViewCounts({ ...getOfferViewCounts(), [offerId]: viewCountData })
}

function cullExpiredViewCounts(viewCounts: OfferViewCounts) {
  const now = Date.now()
  const newViewCounts: OfferViewCounts = {}
  for (const [offerId, viewCountData] of Object.entries(viewCounts)) {
    if (viewCountData.savePromptShownAt || now - viewCountData.lastViewedAt < VIEW_RESET_GAP) {
      newViewCounts[offerId] = viewCountData
    }
  }
  return newViewCounts
}

export function recordOfferView(offerId: string): OfferViewCountData {
  const viewCounts = cullExpiredViewCounts(getOfferViewCounts())
  let offerViewCountData = viewCounts[offerId]
  let shouldIncrementViewCount = true
  const now = Date.now()

  if (offerViewCountData) {
    const viewGap = now - offerViewCountData.lastViewedAt
    if (viewGap < MIN_VIEW_GAP) {
      shouldIncrementViewCount = false
    }
  } else {
    offerViewCountData = { viewCount: 0, lastViewedAt: 0 }
  }

  const newData = {
    ...offerViewCountData,
    viewCount: offerViewCountData.viewCount + (shouldIncrementViewCount ? 1 : 0),
    lastViewedAt: now,
  }
  saveOfferViewCounts({
    ...viewCounts,
    [offerId]: newData,
  })
  return newData
}

export function recordSavePromptShown(offerId: string) {
  const offerViewCountData = getOfferViewCountData(offerId) ?? { viewCount: 0, lastViewedAt: 0 }

  offerViewCountData.savePromptShownAt = Date.now()
  saveOfferViewCountData(offerId, offerViewCountData)
}
